import type { Selector } from 'reselect';
import { createSelector } from 'reselect';

import type {
  IIndexedPaymentTerms,
  IIndexedSeedTypes,
  IIndexedTradingSeasons,
  ISeedMetadataState,
  ISeedPaymentTerm,
  ISeedType,
  ITradingSeason,
} from './types';

const createAppSelector = createSelector.withTypes<ISeedMetadataState>();

export const selectTradingSeasons = (state: ISeedMetadataState) => state.seasons.data;
export const selectSeedTypes = (state: ISeedMetadataState) => state.config.data.seedTypes;
export const selectPaymentTerms = (state: ISeedMetadataState) => state.config.data.paymentTerms;

export const selectIndexedTradingSeasons: Selector<ISeedMetadataState, IIndexedTradingSeasons> =
  createAppSelector(selectTradingSeasons, (tradingSeasons) =>
    tradingSeasons.reduce((indexed: IIndexedTradingSeasons, tradingSeason: ITradingSeason) => {
      if (tradingSeason.tradingSeasonCode) {
        indexed[tradingSeason.tradingSeasonCode] = tradingSeason;
      }
      return indexed;
    }, {}),
  );

export const selectTradingSeason: Selector<ISeedMetadataState, ITradingSeason> = createAppSelector(
  [
    selectIndexedTradingSeasons,
    (_: ISeedMetadataState, tradingSeasonCode: string) => tradingSeasonCode,
  ],
  (indexedTradingSeasons, tradingSeasonCode) => indexedTradingSeasons[tradingSeasonCode],
);

export const selectIndexedSeedTypes: Selector<ISeedMetadataState, IIndexedSeedTypes> =
  createAppSelector(selectSeedTypes, (seedTypes) =>
    seedTypes.reduce((indexed: IIndexedSeedTypes, seedType: ISeedType) => {
      if (seedType.value) {
        indexed[seedType.value] = seedType;
      }
      return indexed;
    }, {}),
  );

export const selectSeedType: Selector<ISeedMetadataState, ISeedType> = createAppSelector(
  [selectIndexedSeedTypes, (_: ISeedMetadataState, seedTypeCode: string) => seedTypeCode],
  (indexedSeedTypes, seedTypeCode) => indexedSeedTypes[seedTypeCode],
);

export const selectIndexedPaymentTerms: Selector<ISeedMetadataState, IIndexedPaymentTerms> =
  createAppSelector(selectPaymentTerms, (paymentTerms) =>
    paymentTerms.reduce((indexed: IIndexedPaymentTerms, paymentTerm: ISeedPaymentTerm) => {
      if (paymentTerm.value) {
        indexed[paymentTerm.value] = paymentTerm;
      }
      return indexed;
    }, {}),
  );

export const selectPaymentTerm: Selector<ISeedMetadataState, ISeedPaymentTerm> = createAppSelector(
  [
    selectIndexedPaymentTerms,
    (_: ISeedMetadataState, paymentTermsCode: string) => paymentTermsCode,
  ],
  (indexedPaymentTerms, paymentTermsCode) => indexedPaymentTerms[paymentTermsCode],
);
